// スタンプラリーの API (#1138)。イベント系機能はここに追随。
// 配置ルールは index.ts のヘッダ参照。
import { API_BASE, ApiError, apiFetch, trackedFetch } from './_client'

// ---- スタンプラリー (#1126) 型定義 ----
export interface StampRegion {
  id: string
  name: string
  centerLat: number
  centerLng: number
  radiusM: number
}

export interface StampBoothLink {
  type: 'instagram' | 'hp' | 'other'
  url: string
  label?: string
  isPrimary?: boolean
}

export interface StampBooth {
  id: string
  name: string
  kana: string
  description: string
  category: string
  thumbnailUrl: string
  boothNo: string
  displayOrder: number
  links: StampBoothLink[]
  primaryLinkUrl: string | null
  /** #1886 ブースごとの特典。空なら画面に枠を出さない。 */
  benefit?: string
  /** #1886 営業用のサンプル。公開したイベントではサーバが返さない。 */
  isSample?: boolean
  /** #1898 どの企画のブースか。イベント詳細は複数の企画の出店者を並べるので要る。 */
  eventId?: string
  stamped?: boolean
}

export interface StampEvent {
  id: string
  // 未指定 = qr (#1154 以前の互換)。'external' = 汎用外部アクティビティ (#1159)
  type?: 'qr' | 'ar' | 'external'
  regionId: string
  name: string
  description: string
  startsAt: string
  endsAt: string
  stampGoal: number
  // 外部アクティビティ設定 (#1159)。'ar' は未設定ならフロント既定値
  // (VITE_AR_WEBAR_URL / AR宝探し文言) にフォールバック
  activityUrl?: string
  activityLabel?: string
  ctaLabel?: string
  /** 'external' で進捗を記録しない (リンクで遊ぶだけ)。カードは説明と起動ボタンだけ。 */
  linkOnly?: boolean
  // イベントそのものの説明。description はカードの1〜2行、overview は詳細ページ本文。
  // infoUrl があれば主催者の HP 等を優先し、無ければアプリ内の詳細ページを使う。
  overview?: string
  infoUrl?: string
  /** #1886 「詳細をみる」で公式サイトへ飛ばすか。既定 false = アプリ内の詳細ページ。 */
  infoUrlPrimary?: boolean
  /** #1869 リハーサル受付中か。スタンプ帳へ戻るときの名指しが要るかの判断に使う。 */
  rehearsalOpen?: boolean
}

export interface StampPrizeSummary {
  name: string
  imageUrl: string
  totalStock: number
  remaining: number
  status: 'ok' | 'low' | 'sold_out'
}

/** #1871 参加抽選の残数 (客向け)。無効・未設定なら null。 */
export interface StampLotterySummary {
  name: string
  imageUrl: string
  winCount: number
  remaining: number
  /**
   * くじの枚数と残り。**当選率の分母はこれ。**
   *
   * 出さないと「当たり 10 本中 残り 10 本」と「当選率 5%」が並んで意味が
   * 通らない（10/10 を計算されてしまう）。枚数が見えれば 10 ÷ 200 を
   * 自分で確かめられる。
   */
  drawTotal: number
  remainingDraws: number
  /** 次の 1 人が当たる確率 (%)。残数方式なので引かれるほど変動する。 */
  oddsPercent: number
  /**
   * #2490 合言葉が要る抽選か。開催者の LINE 公式アカウントのあいさつメッセージに
   * 書かれた合言葉を入れた人だけが引ける。**合言葉そのものはサーバが返さない。**
   */
  keywordRequired?: boolean
  /** 友だち追加のリンク (https://lin.ee/... など)。空なら出さない。 */
  lineAddUrl?: string
  /** 公式アカウントの名前 (「〇〇 を友だち追加」と出す)。 */
  lineAccountName?: string
}

/** #2490 合言葉を入れた結果。 */
export interface StampLotteryKeywordResult {
  accepted: boolean
  /** まだスタンプが無いので引けていない (最初のスキャンで引ける)。 */
  needsStamp: boolean
  /** 引けた (または既に引いていた) 結果。スタンプ 0 なら null。 */
  lottery: StampMyDraw | null
  summary: StampLotterySummary | null
}

/** #1871 自分の抽選結果。未抽選なら null。はずれは code を持たない。 */
export interface StampMyDraw {
  result: 'win' | 'lose'
  code: string | null
  status: 'issued' | 'lost' | 'redeemed'
  drawnAt: string | null
  redeemedAt: string | null
  /** このレスポンスで初めて引かれたか (演出の出し分け用)。 */
  justDrawn?: boolean
}

export interface StampProgress {
  stampCount: number
  goal: number
  achieved: boolean
  stampedStoreIds: string[]
}

/**
 * 枠の表示位置。top / list はイベントのカードの中、#2492 popup (開いたときに手前に出す) と
 * bottom (画面の下に固定) はスタンプ帳の画面全体に出す。
 */
export type StampSlotPlacement = 'top' | 'list' | 'popup' | 'bottom'

export interface StampSlot {
  id: string
  type: 'banner' | 'ad'
  placement: StampSlotPlacement
  imageUrl: string
  linkUrl: string
  priority: number
}

/**
 * #1898 イベント (親)。企画 (スタンプラリー / AR宝探し / ミニゲーム …) を束ねる。
 *
 * **開催期間・地域は所属する企画から決まる** (親は持たない)。
 */
export interface StampEventGroup {
  id: string
  name: string
  description: string
  overview: string
  infoUrl: string
  infoUrlPrimary: boolean
  /** 会場 (任意)。カードの開催期間の下に出す */
  venue?: string
  regionId: string
  startsAt: string
  endsAt: string
  /** 企画のどれかがリハーサル受付中か。戻るときにイベントを名指しする必要がある。 */
  rehearsalOpen: boolean
}

// 外部アクティビティ (#1154/#1159) の企画 — QRラリーとは独立した進捗・景品を持つ
export interface StampArEventBlock {
  kind?: 'activity'
  event: StampEvent
  spotCount: number
  prize: StampPrizeSummary | null
  /** #1871 参加抽選 (AR も QR と同じ仕組み。設定していないイベントでは null) */
  lottery?: StampLotterySummary | null
  myDraw?: StampMyDraw | null
  progress: StampProgress | null
}

/** 内蔵 QR ラリーの企画。 */
export interface StampQrProgramBlock {
  kind: 'qr'
  event: StampEvent
  stores: StampBooth[]
  prize: StampPrizeSummary | null
  lottery: StampLotterySummary | null
  myDraw: StampMyDraw | null
  /**
   * #2490 合言葉を入れたか。**合言葉の要らない抽選・ゲスト・未ログインは null** —
   * false (入力欄を出す) と null (何も出さない) を分けるため。
   */
  keywordEntered?: boolean | null
  progress: StampProgress | null
}

export type StampActivityProgramBlock = StampArEventBlock & { kind: 'activity' }
export type StampProgramBlock = StampQrProgramBlock | StampActivityProgramBlock

/** #1898 開催中のイベント 1 件。中に企画が並ぶ (QR ラリーが先)。 */
export interface StampEventBlock {
  event: StampEventGroup
  /** イベント枠 (バナー / PR 枠)。**イベントに付く** — 企画のブロックには無い */
  slots: StampSlot[]
  programs: StampProgramBlock[]
}

/** #1886 開催予定の企画 1 件。開催中のセクションと同じ材料を持つ。 */
export interface StampUpcomingBlock {
  kind: 'qr' | 'activity'
  event: StampEvent
  prize: StampPrizeSummary | null
  lottery: StampLotterySummary | null
  storeCount: number
}

/** #1898 開催予定のイベント 1 件。 */
export interface StampUpcomingEventBlock {
  event: StampEventGroup
  slots: StampSlot[]
  programs: StampUpcomingBlock[]
}

/** イベント詳細に並ぶ企画 1 件。 */
export interface StampEventInfoProgram {
  kind: 'qr' | 'activity'
  event: StampEvent
  prize: StampPrizeSummary | null
  lottery: StampLotterySummary | null
  storeCount: number
}

/** イベント詳細（公開・期間もジオフェンスも見ない）。告知は開催前から出るので、
 *  ここを開催中だけにすると告知から来た人が何も読めない。
 *
 *  #1898 イベント (親) のページ。企画の ID で開いても所属イベントが返る。 */
export interface StampEventInfo {
  event: StampEventGroup
  region: StampRegion | null
  slots: StampSlot[]
  programs: StampEventInfoProgram[]
  storeCount: number
  /** #1886 参加ブース。**全件返る**。#1898 でイベント内の QR 企画の出店者をまとめて並べる。 */
  stores: StampBooth[]
}

export interface StampContext {
  available: boolean
  // 'upcoming' = まだ始まっていないイベントがある（予告として出す）
  reason: 'no_region' | 'no_event' | 'upcoming' | null
  region: StampRegion | null
  /** #1898 開催中のイベント。企画はイベントごとにまとまって並ぶ。 */
  events: StampEventBlock[]
  /** #1886 開催予定。**開催中の有無に関わらず**返る。中身は開催中と同じ材料。 */
  upcomingEvents: StampUpcomingEventBlock[]
  /** 終わってから 90 日以内のイベント (TOP の「終了」タブ)。読むだけなので予定と同じ材料。
   *  API が古いと無いので省略可 */
  endedEvents?: StampUpcomingEventBlock[]
}

export interface StampTicket {
  code: string
  status: 'issued' | 'redeemed' | 'void'
  eventId: string
  issuedAt: string | null
  redeemedAt: string | null
}

export interface StampArLaunchResult {
  token: string
  expiresAt: string
  event: StampEvent
}

export interface StampArSpot {
  id: string
  name: string
  description: string
  lat: number | null
  lng: number | null
  markerType: 'image' | 'qr'
  markerRef: string
  displayOrder: number
  characterUrl?: string
  /** 'pr' = 宣伝・演出のみ (開封不可)。description が宣伝文章になる。
   *  'creation' = みんなのキャラ (当日に来場者と作って置いたキャラ・開封不可)
   *  'board' = 掲示板 (参加者がメモを貼る。読む・貼るのは AR の中だけ) */
  spotKind?: 'treasure' | 'pr' | 'creation' | 'board'
  prLinkUrl?: string
  /** みんなのキャラを作った人のニックネーム */
  creatorName?: string
  sponsorName?: string
  sponsorLinkUrl?: string
}

/** 自分が獲得したスポット (#1154 follow-up)。未獲得は含まれず、座標も返らない。 */
export interface StampCollectedSpot {
  id: string
  name: string
  description: string
  characterUrl?: string
  /** 宝箱は開けたもの、'pr' / 'creation' はボールで捕まえたもの */
  spotKind?: 'treasure' | 'pr' | 'creation'
  /** PR キャラの「くわしく見る」の遷移先 */
  prLinkUrl?: string
  /** みんなのキャラを作った人のニックネーム */
  creatorName?: string
  /** 協賛企業。**キャラを出すだけでは協賛の売り物にならない** — 見つけた人にも、
   *  スクショを見た人にも、どこの企業のキャラかが伝わる必要がある。 */
  sponsorName?: string
  sponsorLinkUrl?: string
}

export interface StampArSpotsResult {
  event: StampEvent
  spots: StampArSpot[]
  progress: StampProgress
  openedSpotIds: string[]
}

export interface StampArCompleteResult {
  result: 'stamped' | 'already' | 'geofence_denied'
  stamp: {
    awarded: boolean
    alreadyStamped: boolean
    stampCount: number
    goal: number
    achieved: boolean
  }
}

export interface StampScanResult {
  result: 'stamped' | 'already' | 'anonymous' | 'geofence_denied'
  store: StampBooth
  event: StampEvent
  prize: StampPrizeSummary | null
  loginRequired: boolean
  stamp: {
    awarded: boolean
    alreadyStamped: boolean
    stampCount: number
    goal: number
    achieved: boolean
  } | null
  /** #1871 このスキャンで引いた (または既に引いていた) 抽選結果。対象外なら null。 */
  lottery?: StampMyDraw | null
  /**
   * #1870 このイベントに抽選が**有るか**。
   *
   * `lottery` は引いた結果なので、ゲスト (対象外) と抽選の無いイベントが
   * どちらも null になり区別できない。ゲストに登録を勧めてよいのは前者だけ。
   */
  lotteryAvailable?: boolean
  /**
   * #2490 合言葉を入れていないので引けなかったか。スタンプを押した直後に
   * 「合言葉を入れると抽選に参加できます」と出すのに使う。
   */
  lotteryNeedsKeyword?: boolean
}

/**
 * #1997 PR枠の計測を送る。**結果を見ない・失敗しても何もしない。**
 *
 * `sendBeacon` を使うのは、クリックの直後に画面が外部サイトへ飛ぶため。
 * 普通の fetch だと**遷移で中断されてクリックが数えられない**ことがある
 * (iOS Safari は中断を `TypeError: Load failed` にする)。sendBeacon は
 * ブラウザが離脱後も送り切る。
 *
 * `trackedFetch` を通さないのも意図的。通信断の通知を Sentry に送る仕組みが
 * 入っているが (#1624)、**計測の取りこぼしは知らせる価値が無い** — 電波の
 * 悪い会場でノイズになるだけで、直せることも無い。
 */
function sendMetricBeacon(url: string): void {
  try {
    if (navigator.sendBeacon?.(url)) return
  } catch {
    // sendBeacon が使えない / 拒否された環境は fetch に落とす
  }
  void fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
}

function sendSlotMetric(slotId: string, kind: 'impression' | 'click'): void {
  sendMetricBeacon(`${API_BASE}/stamp/slots/${encodeURIComponent(slotId)}/${kind}`)
}

export const stampApi = {
  // =====================================================================
  // スタンプラリー (#1126) — 顧客側。認証は apiFetch が自動付与（匿名でも可、
  // その場合サーバは loginRequired: true を返しスタンプは付与しない）
  // =====================================================================
  getStampRegions: async (): Promise<{ regions: StampRegion[] }> => {
    const res = await apiFetch('/stamp/regions')
    return res.json()
  },
  getStampContext: async (params: { lat?: number; lng?: number; regionId?: string; eventId?: string }): Promise<StampContext> => {
    const qs = new URLSearchParams()
    if (params.lat !== undefined && params.lng !== undefined) {
      qs.set('lat', String(params.lat))
      qs.set('lng', String(params.lng))
    }
    if (params.regionId) qs.set('regionId', params.regionId)
    // #1869 リハーサル受付中のイベントは公開前なので地域から引けない。名指しで開く。
    if (params.eventId) qs.set('eventId', params.eventId)
    const res = await apiFetch(`/stamp/context${qs.toString() ? `?${qs}` : ''}`)
    return res.json()
  },
  getStampEventInfo: async (eventId: string): Promise<StampEventInfo> => {
    const res = await apiFetch(`/stamp/events/${eventId}`)
    return res.json()
  },
  scanStampToken: async (token: string, coords?: { lat: number; lng: number }): Promise<StampScanResult> => {
    const res = await apiFetch('/stamp/scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, lat: coords?.lat, lng: coords?.lng }),
    })
    return res.json()
  },
  getStampProgress: async (eventId: string): Promise<{
    progress: StampProgress
    prize: StampPrizeSummary | null
    lottery?: StampLotterySummary | null
    myDraw?: StampMyDraw | null
    keywordEntered?: boolean | null
  }> => {
    const res = await apiFetch(`/stamp/progress?eventId=${encodeURIComponent(eventId)}`)
    return res.json()
  },
  /**
   * #2490 開催者の LINE 公式アカウントで届いた合言葉を入れる (アカウント限定)。
   * 400 = 合言葉が違う / 429 = 間違いが続いた / 410 = 開催中でない。
   */
  enterStampLotteryKeyword: async (eventId: string, keyword: string): Promise<StampLotteryKeywordResult> => {
    const res = await apiFetch('/stamp/lottery/keyword', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, keyword }),
    })
    return res.json()
  },
  claimStampTicket: async (eventId: string): Promise<{ ticket: StampTicket; created: boolean; prize: StampPrizeSummary | null }> => {
    const res = await apiFetch('/stamp/ticket/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId }),
    })
    return res.json()
  },
  /** #1870 ゲストで集めたスタンプ・チケットをアカウントへ引き継ぐ。
   *  ログイン直後に 1 回だけ呼ぶ。QR ラリーも AR もまとめて移る (種別で分岐しない)。 */
  claimGuestStamps: async (guestId: string): Promise<{
    movedProgress: string[]
    mergedProgress: string[]
    movedTickets: string[]
    keptTickets: string[]
  }> => {
    const res = await apiFetch('/stamp/claim-guest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId }),
    })
    return res.json()
  },
  getMyStampTicket: async (eventId: string): Promise<{ ticket: StampTicket | null; prize: StampPrizeSummary | null }> => {
    const res = await apiFetch(`/stamp/ticket?eventId=${encodeURIComponent(eventId)}`)
    return res.json()
  },

  // ---- AR宝探しラリー (#1154) — 起動は要ログイン。complete は通常 WebAR が
  //      直接叩くが、AR 非対応端末のフォールバック (ArGate) では本体からも呼ぶ ----
  launchArSession: async (
    params: { eventId?: string; spotId?: string; spotQrVersion?: number },
  ): Promise<StampArLaunchResult> => {
    const res = await apiFetch('/stamp/ar/launch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    return res.json()
  },
  /** 何を集めたかを見せるための一覧。スタンプの数だけでは、どのキャラを
   *  見つけたのか分からず、用意したキャラが記録に残らない。 */
  getCollectedSpots: async (eventId: string): Promise<{ spots: StampCollectedSpot[] }> => {
    const res = await apiFetch(`/stamp/events/${encodeURIComponent(eventId)}/collected`)
    return res.json()
  },
  getArSpots: async (arToken: string): Promise<StampArSpotsResult> => {
    // 資格 = AR 起動トークン。apiFetch は Firebase トークンで Authorization を
    // 上書きするため、ここだけ素の fetch を使う (WebAR と同じ叩き方)。
    const res = await trackedFetch(`${API_BASE}/stamp/ar/spots`, {
      headers: { Authorization: `Bearer ${arToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'AR情報の取得に失敗しました。')
    return res.json()
  },
  completeArSpot: async (
    token: string,
    spotId: string,
    coords?: { lat: number; lng: number },
  ): Promise<StampArCompleteResult> => {
    const res = await apiFetch('/stamp/ar/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, spotId, lat: coords?.lat, lng: coords?.lng }),
    })
    return res.json()
  },

  // ---- スタンプラリー 管理側 (adminGetAiBudget と同じ idToken 明示パターン) ----
  adminStampRequest: async (
    idToken: string,
    path: string,
    options: RequestInit = {},
  ): Promise<Response> => {
    // 管理 CRUD はエンドポイント数が多いので薄い共通口を用意 (メソッド爆発回避)
    return apiFetch(`/admin/stamp${path}`, {
      ...options,
      headers: {
        // FormData (multipart) はブラウザに boundary 付き Content-Type を任せる
        ...(typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers as Record<string, string> || {}),
        'Authorization': `Bearer ${idToken}`,
      },
    })
  },

  // ---- PR枠の計測 (#1997) ----
  // ここだけ apiFetch を通さない (上の sendSlotMetric のコメント参照)。
  // 認証も要らない — 枠はゲストにも出るので、ログインを求めた時点で数字が実態から外れる。

  /** PR枠が実際に画面に出たとき。**表示時に数える** (配信時ではない)。 */
  recordSlotImpression: (slotId: string): void => sendSlotMetric(slotId, 'impression'),

  /** PR枠が押されたとき。外部リンク / アプリ内遷移 / 拡大表示のどれでも 1 回。 */
  recordSlotClick: (slotId: string): void => sendSlotMetric(slotId, 'click'),

  /** #2111 ブースの説明が開かれたとき。**開くたびに 1 回** (同じ人の読み直しも入る)。
   *  取りこぼしても画面には影響しないので、PR枠と同じく beacon で投げっぱなしにする。 */
  recordBoothView: (boothId: string): void =>
    sendMetricBeacon(`${API_BASE}/stamp/booths/${encodeURIComponent(boothId)}/view`),

  // ---- ポイント 管理側 (#1444 / #1445 / #1433) ----
  // スタンプラリーと同じく、エンドポイント数が多いので薄い共通口だけ置く。
  // 型付きヘルパーは services/adminPointsApi.ts に集約する。
  adminPointsRequest: async (
    idToken: string,
    path: string,
    options: RequestInit = {},
  ): Promise<Response> => {
    return apiFetch(`/admin${path}`, {
      ...options,
      headers: {
        ...(typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers as Record<string, string> || {}),
        'Authorization': `Bearer ${idToken}`,
      },
    })
  },
}
